<template>
  <VSearchableDropdown
    v-model="proxyModelValue"
    class="property-status"
    label-key="name"
    value-key="id"
    panel-width="350px"
    floating
    floating-strategy="absolute"
    :floating-min-height="300"
    no-results-text="No data found"
    label="Property status"
    arrow-icon-class-name="fr fr-chevron-down"
    :multiple="true"
    :searchable="true"
    :close-on-select="false"
    :fetch-items="fetchItems"
  />
</template>

<script setup lang="ts">
  // Types
  import type { WritableComputedRef } from 'vue';
  import type { TEntityStatus } from '~/misc/opportunity/types';
  import type { TListingsStore } from '~/misc/listings/store/types';
  import type {
    IVSearchableDropdownFetchResult,
    TVSearchableDropdownFetchFn,
  } from '~/misc/v-searchable-dropdown/types';
  import type { TOpportunityDataClientModel } from '~/misc/listings/types';

  // Services
  import { OpportunityDataClient } from '~/misc/opportunity/services/opportunity-data-client';
  import { AbstractDataClient } from '~/misc/services/data-client';

  // Store
  import { useListingsStore } from '~/misc/listings/store';

  // Components
  import VSearchableDropdown from '~/misc/v-searchable-dropdown';

  const props = defineProps({
    modelValue: {
      type: Array as PropType<number[]>,
      required: true,
    },
    dataClient: {
      type: Object as PropType<AbstractDataClient<TOpportunityDataClientModel>>,
      required: false,
      default: () => new OpportunityDataClient(),
    },
  });

  const listingsStore: TListingsStore = useListingsStore();

  const { error: showError } = useToast();

  const data: Ref<TEntityStatus[]> = ref<TEntityStatus[]>([]);

  const isLoading: Ref<boolean> = ref<boolean>(false);

  const proxyModelValue: WritableComputedRef<(number | string)[]> = computed({
    get(): (number | string)[] {
      return props.modelValue;
    },
    set(value: (number | string)[]): void {
      emit('update:modelValue', value);
    },
  });

  const setData = (value: TEntityStatus[]): void => {
    data.value = value;
  };

  const setIsLoading = (state: boolean): void => {
    isLoading.value = state;
  };

  const fetchItems: TVSearchableDropdownFetchFn = async (
    query: string,
    page: number
  ): Promise<IVSearchableDropdownFetchResult<TEntityStatus>> => {
    // Load all data on first request (page 0) if not already loaded
    if (page === 0 && data.value.length === 0) {
      // Check if entity statuses are already cached in the store
      if (listingsStore.entityStatuses.length > 0) {
        setData(listingsStore.entityStatuses);
      } else {
        // Fetch from API if not cached
        try {
          setIsLoading(true);
          const response: TEntityStatus[] = await props.dataClient.request<TEntityStatus[]>(
            'entityStatuses'
          );
          setData(response);
          listingsStore.setEntityStatuses(response);
        } catch {
          showError({ message: 'Sorry, an error occurred while fetching property statuses.' });

          return {
            items: [],
            hasMore: false,
            total: 0,
          };
        } finally {
          setIsLoading(false);
        }
      }

      // Auto-select all statuses when none are currently selected (default state)
      if (props.modelValue.length === 0 && data.value.length > 0) {
        proxyModelValue.value = data.value.map((status: TEntityStatus) => status.id);
      }
    }

    // Filter data based on search query (client-side filtering)
    const filteredData: TEntityStatus[] = query
      ? data.value.filter((status: TEntityStatus) =>
          status.name.toLowerCase().includes(query.toLowerCase())
        )
      : data.value;

    return {
      items: filteredData,
      hasMore: false, // All data is loaded at once, no pagination
      total: filteredData.length,
    };
  };

  const emit = defineEmits<{
    (e: 'update:modelValue', value: (number | string)[]): void;
  }>();
</script>
